import { useState, useEffect } from 'react';
import Badge from '../common/Badge';
import Button from '../common/Button';
import LoadingSpinner from '../common/LoadingSpinner';
import betslipService from '../../services/betslipService';

const BettingAnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(null); // null = all time
  const [sportFilter, setSportFilter] = useState(null);
  const [resolving, setResolving] = useState(false);

  const timeRanges = [
    { value: null, label: 'All Time' },
    { value: 7, label: 'Last 7 Days' },
    { value: 30, label: 'Last 30 Days' },
    { value: 90, label: 'Last 90 Days' },
  ];

  const sports = [
    { value: null, label: 'All Sports' },
    { value: 'nba', label: 'NBA' },
    { value: 'nfl', label: 'NFL' },
    { value: 'nhl', label: 'NHL' },
    { value: 'mlb', label: 'MLB' },
  ];

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const data = await betslipService.getAnalytics(timeRange, sportFilter);
      setAnalytics(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, sportFilter]);

  const handleResolve = async () => {
    try {
      setResolving(true);
      const result = await betslipService.resolvePendingBetslips(7);
      alert(`Resolved ${result.summary.resolved} bets: ${result.summary.won} won, ${result.summary.lost} lost`);
      fetchAnalytics();
    } catch (err) {
      console.error('Error resolving bets:', err);
      alert('Failed to resolve bets');
    } finally {
      setResolving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card p-6 text-center">
        <p className="text-accent-red">{error}</p>
        <Button variant="secondary" onClick={fetchAnalytics} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  if (!analytics) return null;

  const { summary, by_bet_type, by_sport, by_sportsbook, by_source, trends, top_players, insights } = analytics;

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-text-primary">Betting Analytics</h2>

        <div className="flex items-center gap-3">
          {/* Time Range Filter */}
          <select
            value={timeRange || ''}
            onChange={(e) => setTimeRange(e.target.value ? parseInt(e.target.value) : null)}
            className="px-3 py-2 bg-bg-card border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent-blue"
          >
            {timeRanges.map(range => (
              <option key={range.label} value={range.value || ''}>{range.label}</option>
            ))}
          </select>

          {/* Sport Filter */}
          <select
            value={sportFilter || ''}
            onChange={(e) => setSportFilter(e.target.value || null)}
            className="px-3 py-2 bg-bg-card border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-accent-blue"
          >
            {sports.map(sport => (
              <option key={sport.label} value={sport.value || ''}>{sport.label}</option>
            ))}
          </select>

          {/* Auto-Resolve Button */}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleResolve}
            disabled={resolving}
          >
            {resolving ? 'Resolving...' : 'Auto-Resolve'}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <SummaryCard
          label="Total Bets"
          value={summary.total_bets}
          icon="🎰"
        />
        <SummaryCard
          label="Win Rate"
          value={`${summary.win_rate.toFixed(1)}%`}
          icon="🎯"
          highlight={summary.win_rate >= 50}
        />
        <SummaryCard
          label="ROI"
          value={`${summary.roi >= 0 ? '+' : ''}${summary.roi.toFixed(1)}%`}
          icon={summary.roi >= 0 ? '📈' : '📉'}
          highlight={summary.roi >= 0}
          negative={summary.roi < 0}
        />
        <SummaryCard
          label="Net Profit"
          value={`${summary.net_profit >= 0 ? '+' : ''}$${Math.abs(summary.net_profit).toFixed(2)}`}
          icon="💰"
          highlight={summary.net_profit >= 0}
          negative={summary.net_profit < 0}
        />
        <SummaryCard
          label="Total Stake"
          value={`$${summary.total_stake.toFixed(2)}`}
          icon="💵"
        />
        <SummaryCard
          label="Record"
          value={`${summary.won}-${summary.lost}-${summary.pushed}`}
          icon="📊"
        />
      </div>

      {/* Insights */}
      {insights && insights.length > 0 && (
        <div className="glass-card p-4">
          <h3 className="text-lg font-semibold text-text-primary mb-3">Insights</h3>
          <div className="space-y-2">
            {insights.map((insight, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border ${
                  insight.type === 'positive' ? 'bg-accent-green/10 border-accent-green/20' :
                  insight.type === 'warning' ? 'bg-accent-red/10 border-accent-red/20' :
                  'bg-accent-blue/10 border-accent-blue/20'
                }`}
              >
                <span className="mr-2">{insight.icon}</span>
                <span className="text-sm text-text-primary">{insight.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analytics Breakdown */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* By Bet Type */}
        <BreakdownCard
          title="By Bet Type"
          data={by_bet_type}
          formatKey={(key) => key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
        />

        {/* By Sport */}
        <BreakdownCard
          title="By Sport"
          data={by_sport}
        />

        {/* By Sportsbook */}
        <BreakdownCard
          title="By Sportsbook"
          data={by_sportsbook}
        />

        {/* By Source/Handicapper */}
        <BreakdownCard
          title="By Source"
          data={by_source}
        />
      </div>

      {/* Trends Chart */}
      {trends && trends.length > 0 && (
        <div className="glass-card p-4">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Weekly Trends</h3>
          <div className="overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              {trends.map((week, index) => (
                <div key={index} className="flex flex-col items-center">
                  <div
                    className={`w-12 rounded-t ${
                      week.net_profit >= 0 ? 'bg-accent-green' : 'bg-accent-red'
                    }`}
                    style={{
                      height: `${Math.max(20, Math.min(100, Math.abs(week.net_profit) * 2))}px`,
                    }}
                  />
                  <span className="text-xs text-text-secondary mt-1">
                    {new Date(week.week).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                  <span className={`text-xs font-medium ${
                    week.net_profit >= 0 ? 'text-accent-green' : 'text-accent-red'
                  }`}>
                    {week.net_profit >= 0 ? '+' : ''}${week.net_profit.toFixed(0)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Top Players */}
      {top_players && top_players.length > 0 && (
        <div className="glass-card p-4">
          <h3 className="text-lg font-semibold text-text-primary mb-4">Player Props Performance</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-secondary border-b border-border">
                  <th className="text-left py-2 px-3">Player</th>
                  <th className="text-left py-2 px-3">Sport</th>
                  <th className="text-center py-2 px-3">Bets</th>
                  <th className="text-center py-2 px-3">Record</th>
                  <th className="text-right py-2 px-3">Net</th>
                  <th className="text-right py-2 px-3">ROI</th>
                </tr>
              </thead>
              <tbody>
                {top_players.slice(0, 10).map((player, index) => (
                  <tr key={index} className="border-b border-border/50 hover:bg-bg-card-hover">
                    <td className="py-2 px-3 font-medium text-text-primary">{player.player_name}</td>
                    <td className="py-2 px-3">
                      <Badge variant="info" size="sm">{player.sport}</Badge>
                    </td>
                    <td className="py-2 px-3 text-center">{player.total_bets}</td>
                    <td className="py-2 px-3 text-center">{player.won}-{player.lost}</td>
                    <td className={`py-2 px-3 text-right font-medium ${
                      player.net_profit >= 0 ? 'text-accent-green' : 'text-accent-red'
                    }`}>
                      {player.net_profit >= 0 ? '+' : ''}${player.net_profit.toFixed(2)}
                    </td>
                    <td className={`py-2 px-3 text-right font-medium ${
                      player.roi >= 0 ? 'text-accent-green' : 'text-accent-red'
                    }`}>
                      {player.roi >= 0 ? '+' : ''}{player.roi.toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Summary Card Component
const SummaryCard = ({ label, value, icon, highlight = false, negative = false }) => (
  <div className="glass-card p-4 text-center">
    <div className="text-2xl mb-1">{icon}</div>
    <div className={`text-xl font-bold ${
      negative ? 'text-accent-red' : highlight ? 'text-accent-green' : 'text-text-primary'
    }`}>
      {value}
    </div>
    <div className="text-xs text-text-secondary">{label}</div>
  </div>
);

// Breakdown Card Component
const BreakdownCard = ({ title, data, formatKey = (k) => k }) => {
  if (!data || Object.keys(data).length === 0) return null;

  const sortedEntries = Object.entries(data)
    .sort((a, b) => b[1].net_profit - a[1].net_profit);

  return (
    <div className="glass-card p-4">
      <h3 className="text-lg font-semibold text-text-primary mb-3">{title}</h3>
      <div className="space-y-2">
        {sortedEntries.map(([key, stats]) => (
          <div key={key} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary">{formatKey(key)}</span>
              <span className="text-xs text-text-secondary">
                ({stats.won}-{stats.lost})
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-text-secondary">
                {stats.win_rate?.toFixed(0)}% WR
              </span>
              <span className={`text-sm font-medium ${
                stats.roi >= 0 ? 'text-accent-green' : 'text-accent-red'
              }`}>
                {stats.roi >= 0 ? '+' : ''}{stats.roi?.toFixed(1)}% ROI
              </span>
              <span className={`text-sm font-bold ${
                stats.net_profit >= 0 ? 'text-accent-green' : 'text-accent-red'
              }`}>
                {stats.net_profit >= 0 ? '+' : ''}${Math.abs(stats.net_profit).toFixed(2)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BettingAnalyticsDashboard;
